import { ChangeDetectionStrategy, Component, inject, signal, OnInit, OnDestroy, computed } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { MessageModule } from 'primeng/message';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { AuthService, StateService } from '@cosmos/angular/utils';
import { AuthLayoutComponent } from '../auth-layout/auth-layout.component';
import { environment } from 'environments/environment';

@Component({
  selector: 'cosmos-validate',
  imports: [
    TranslatePipe, RouterModule, ReactiveFormsModule,
    FloatLabelModule, InputTextModule, ButtonModule, MessageModule,
    DividerModule, AuthLayoutComponent,
  ],
  templateUrl: './validate.component.html',
  styleUrl: './validate.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValidateComponent implements OnInit, OnDestroy {

  // ── services ──────────────────────────────────────────────
  private readonly stateService = inject(StateService);
  private readonly authService  = inject(AuthService);
  private readonly router       = inject(Router);
  private readonly route        = inject(ActivatedRoute);
  private readonly http         = inject(HttpClient);
  private readonly fb           = inject(FormBuilder);

  // ── reactive user signal ──────────────────────────────────
  protected readonly user = toSignal(this.stateService.currentUser$, {
    initialValue: this.stateService.getCurrentUser(),
  });

  protected readonly emailValidated = computed(() => !!this.user()?.validated);
  protected readonly phoneValidated = computed(() => !!(this.user() as any)?.phoneValidated);
  protected readonly userId         = computed(() => (this.user() as any)?._id ?? null);

  // ── email section state ───────────────────────────────────
  protected readonly emailValidating    = signal(false);
  protected readonly emailValidateError = signal(false);
  protected readonly emailResending     = signal(false);

  // ── phone section state ───────────────────────────────────
  protected readonly otpLoading   = signal(false);
  protected readonly otpResending = signal(false);
  protected readonly otpError     = signal<string | null>(null);

  readonly otpForm = this.fb.group({
    code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
  });

  private userSub: Subscription;

  // ─────────────────────────────────────────────────────────
  ngOnInit(): void {
    const user = this.stateService.getCurrentUser();
    if (!user) { this.router.navigate(['/login']); return; }

    // Already fully validated → go to dashboard
    if (user.validated && (user as any).phoneValidated) {
      this.router.navigate(['/']);
      return;
    }

    // Watch for both becoming validated → navigate automatically
    this.userSub = this.stateService.currentUser$.subscribe(u => {
      if (u?.validated && (u as any)?.phoneValidated) {
        this.router.navigate(['/']);
      }
    });

    // Auto-validate email if a token is in the query string
    const token = this.route.snapshot.queryParamMap.get('token');
    if (token && !user.validated) {
      this.validateEmailToken(token);
    }

    // Phone OTP is NOT auto-sent here: registration already sends one, and
    // auto-sending again on every landing on this page (e.g. straight after
    // signup) doubles up SMS. The user can request one via the resend button.
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
  }

  // ── email actions ─────────────────────────────────────────

  // in component email token validation flow 
  // -> this is a future replacement to the own verify-email component
  // -> verify-email will be deprecated in favor of this more generic one 
  //    that can handle both email and phone validation in the same place 
  
  private validateEmailToken(token: string): void {
    this.emailValidating.set(true);
    this.emailValidateError.set(false);
    this.http
      .post(`${environment.API_URL}/token/validate-email`, { token }, { observe: 'response' })
      .subscribe({
        next: () => {
          this.emailValidating.set(false);
          // Refresh the user object so validated flag is picked up by the signal
          this.authService.updateCurrentUser().subscribe();
        },
        error: () => {
          this.emailValidating.set(false);
          this.emailValidateError.set(true);
        },
      });
  }

  onEmailResend(): void {
    if (!this.userId()) return;
    this.emailResending.set(true);
    this.authService.resendVerificationEmail(this.userId()!).subscribe({
      next: () => this.emailResending.set(false),
      error: () => this.emailResending.set(false),
    });
  }

  onEmailReload(): void {
    this.authService.updateCurrentUser().subscribe();
  }

  // ── phone (OTP) actions ───────────────────────────────────

  onOtpSubmit(): void {
    if (this.otpForm.invalid || !this.userId()) return;
    this.otpLoading.set(true);
    this.otpError.set(null);
    this.authService.verifyPhoneOtp(this.userId()!, this.otpForm.getRawValue().code!).subscribe({
      next: () => {
        this.otpLoading.set(false);
        // Refresh user → the subscription above triggers navigation when both are done
        this.authService.updateCurrentUser().subscribe();
      },
      error: (err: any) => {
        this.otpError.set(err?.error?.message ?? 'auth.verifyPhone.otpError');
        this.otpLoading.set(false);
      },
    });
  }

  onOtpResend(): void {
    if (!this.userId()) return;
    this.otpResending.set(true);
    this.authService.resendPhoneOtp(this.userId()!).subscribe({
      next: () => this.otpResending.set(false),
      error: () => this.otpResending.set(false),
    });
  }
}
